// Ultimate Card Game (UCG) - Archive mode-toggle performance tooling
//
// Step 0 and 1 of docs/ARCHIVE_MODE_TOGGLE_PERFORMANCE_PLAN_2026-09-20.md:
//   - a frame probe that records requestAnimationFrame deltas around the
//     SINGLEPLAYER <-> MULTIPLAYER switch (active only with ?probe=archive), and
//   - A/B switches (archive-perf-* classes on #deck-modal) so every candidate
//     optimization can be toggled per device without a rebuild.
//
// Everything in here is opt-in. Without ?probe=archive, ?archiveperf=... or a
// stored ucg_archive_perf value nothing is created, nothing is measured and no
// class is added: the default behaviour of the Archive is unchanged.
//
// The pure parts (flag parsing, statistics, the probe state machine) take their
// environment as arguments so they run in Node tests; only mountProbeHud() touches
// the DOM.

// The frame rate of the living hangar on phones, and its switches. Measured on a device
// (docs/ARCHIVE_MODE_TOGGLE_PERFORMANCE_PLAN_2026-09-20.md, section 13): the hangar loop is what
// holds the Archive below 60 fps, 30 fps gives back most of it while the hangar keeps moving.
export const MOBILE_HANGAR_FPS = 30;

export const ARCHIVE_PERF_FLAGS = ['fullfps', 'cheap', 'nobay'];
export const ARCHIVE_PERF_STORAGE_KEY = 'ucg_archive_perf';
export const ARCHIVE_PERF_CLASS_PREFIX = 'archive-perf-';

export const ARCHIVE_PERF_FLAG_INFO = {
  fullfps: 'REVERT  hangar loop at 60 fps on phones again (the behaviour before the 30 fps default)',
  cheap: 'OPTION  hangar drawn once, parallax stays live (mobile only): no moving mechs/lights, no per-frame redraw',
  nobay: 'DIAGNOSTIC  hangar loop stopped for good (mobile only): the upper bound for any hangar change',
};

// ---- flags -----------------------------------------------------------------

export function parseArchivePerfFlags(text) {
  if (typeof text !== 'string') return [];
  const out = [];
  for (const part of text.split(/[\s,;|]+/)) {
    const name = part.trim().toLowerCase();
    if (ARCHIVE_PERF_FLAGS.includes(name) && !out.includes(name)) out.push(name);
  }
  return out;
}

export function serializeArchivePerfFlags(flags) {
  return ARCHIVE_PERF_FLAGS.filter((f) => flags.includes(f)).join(',');
}

// URL parameter wins over storage (so a link can pin a configuration for a test
// run); with neither present the result is an empty list.
export function readArchivePerfFlags({ search = '', storage = null } = {}) {
  let params = null;
  try {
    params = new URLSearchParams(search);
  } catch {
    params = null;
  }
  if (params && params.has('archiveperf')) {
    return parseArchivePerfFlags(params.get('archiveperf'));
  }
  try {
    return parseArchivePerfFlags(storage?.getItem?.(ARCHIVE_PERF_STORAGE_KEY) || '');
  } catch {
    return [];
  }
}

export function isArchiveProbeEnabled(search = '') {
  try {
    return new URLSearchParams(search).get('probe') === 'archive';
  } catch {
    return false;
  }
}

// ---- statistics ------------------------------------------------------------

// Nearest-rank percentile on an ascending array.
function percentile(sorted, p) {
  if (sorted.length === 0) return 0;
  const rank = Math.min(sorted.length, Math.max(1, Math.ceil((p / 100) * sorted.length)));
  return sorted[rank - 1];
}

const round1 = (v) => Math.round(v * 10) / 10;

export function computeFrameStats(deltasMs) {
  const deltas = (deltasMs || []).filter((d) => Number.isFinite(d) && d > 0);
  if (deltas.length === 0) {
    return { frames: 0, avg: 0, p50: 0, p95: 0, max: 0, over17: 0, over25: 0, over33: 0, over50: 0, over100: 0 };
  }
  const sorted = [...deltas].sort((a, b) => a - b);
  const sum = deltas.reduce((a, b) => a + b, 0);
  return {
    frames: deltas.length,
    avg: round1(sum / deltas.length),
    p50: round1(percentile(sorted, 50)),
    p95: round1(percentile(sorted, 95)),
    max: round1(sorted[sorted.length - 1]),
    // Thresholds are absolute on purpose: the acceptance criteria of the plan are
    // "p95 <= 20 ms, at most 2 frames > 33 ms" regardless of the display's refresh rate.
    over17: deltas.filter((d) => d > 17).length,
    over25: deltas.filter((d) => d > 25).length,
    over33: deltas.filter((d) => d > 33).length,
    // A display that settles at ~30 Hz delivers every frame at 33.3-34 ms, so > 33 ms
    // then counts half of all frames and says nothing about hitches. Real stalls are
    // two missed 60 Hz intervals (> 50 ms) and clear hitches (> 100 ms).
    over50: deltas.filter((d) => d > 50).length,
    over100: deltas.filter((d) => d > 100).length,
  };
}

// ---- probe -----------------------------------------------------------------

// Records the gaps between animation frames for a fixed window and reports the
// statistics. The environment (raf, cancel, metadata) is injected so the state machine is
// testable; in the browser it defaults to window.requestAnimationFrame.
export class ArchiveFrameProbe {
  constructor({ raf, cancel, setTimer, clearTimer, onResult, getMeta } = {}) {
    this._raf = raf || ((fn) => globalThis.requestAnimationFrame(fn));
    this._cancel = cancel || ((id) => globalThis.cancelAnimationFrame(id));
    this._setTimer = setTimer || ((fn, ms) => globalThis.setTimeout(fn, ms));
    this._clearTimer = clearTimer || ((id) => globalThis.clearTimeout(id));
    this.onResult = onResult || (() => {});
    this._getMeta = getMeta || (() => ({}));
    this._active = null;
    this.results = [];
  }

  get isRecording() {
    return this._active !== null;
  }

  // Starts a recording window. An automatic window (started by the toggle handler) never
  // replaces a running one; a manual window (HUD button) replaces an automatic one.
  begin(label, { durationMs = 1500, auto = false } = {}) {
    if (this._active) {
      if (auto || !this._active.auto) return false;
      this._abort();
    }
    const rec = { label, auto, durationMs, deltas: [], last: null, start: null, id: null, timer: null, resolve: null };
    this._active = rec;
    const step = (t) => {
      if (this._active !== rec) return;
      if (rec.start === null) rec.start = t;
      if (rec.last !== null) rec.deltas.push(t - rec.last);
      rec.last = t;
      if (t - rec.start >= rec.durationMs) {
        this._finish(rec);
        return;
      }
      rec.id = this._raf(step);
    };
    rec.id = this._raf(step);
    // Watchdog: a hidden tab or a throttled display link (low-power mode) may never call
    // back. Report what was seen (possibly nothing) instead of leaving the HUD silent.
    rec.timer = this._setTimer(() => {
      if (this._active !== rec) return;
      try { this._cancel(rec.id); } catch { /* best effort */ }
      this._finish(rec);
    }, durationMs + 750);
    return true;
  }

  // Promise flavour for scripted runs (the sweep). Resolves with the result, or with null
  // when the window could not be started or was replaced by another one.
  beginAsync(label, opts = {}) {
    return new Promise((resolve) => {
      if (!this.begin(label, opts)) {
        resolve(null);
        return;
      }
      this._active.resolve = resolve;
    });
  }

  _abort() {
    if (this._active?.resolve) this._active.resolve(null);
    if (this._active?.id != null) {
      try { this._cancel(this._active.id); } catch { /* best effort */ }
    }
    if (this._active?.timer != null) {
      try { this._clearTimer(this._active.timer); } catch { /* best effort */ }
    }
    this._active = null;
  }

  _finish(rec) {
    if (rec.timer != null) {
      try { this._clearTimer(rec.timer); } catch { /* best effort */ }
    }
    this._active = null;
    const result = { label: rec.label, durationMs: rec.durationMs, ...computeFrameStats(rec.deltas), ...this._getMeta() };
    this.results.push(result);
    if (this.results.length > 400) this.results.shift();
    this.onResult(result);
    if (rec.resolve) rec.resolve(result);
  }
}

// ---- sweep -----------------------------------------------------------------

// One measurement run over a list of flag configurations, so a test on the phone is a
// single button press instead of ~100 manual taps. Per configuration: settle, idle window,
// three SP->MP + MP->SP switches (each in its own window), one rapid burst. Candidates are
// interleaved with baselines (A B A C A ...) because the first device run showed that time
// alone moves the numbers as much as any switch: the first and last baseline of that run
// differed by 20 fps at idle. The empty-page reference at both ends measures what the display
// and OS deliver with nothing to draw, i.e. the ceiling no app change can beat.
export const SWEEP_CONFIGS = [
  { name: 'reference(empty page)', reference: true },
  { name: 'baseline', flags: [] },
  { name: 'fullfps', flags: ['fullfps'] },
  { name: 'baseline', flags: [] },
  { name: 'cheap', flags: ['cheap'] },
  { name: 'baseline', flags: [] },
  { name: 'nobay', flags: ['nobay'] },
  { name: 'baseline', flags: [] },
  { name: 'reference(empty page)', reference: true },
];

const median = (values) => {
  const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (v.length === 0) return 0;
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : round1((v[mid - 1] + v[mid]) / 2);
};

export function summarizeSweepConfig(name, { idle, toggles = [], rapid }) {
  const fps = idle && idle.durationMs ? round1(idle.frames / (idle.durationMs / 1000)) : 0;
  const sum = (key) => toggles.reduce((a, t) => a + (t[key] || 0), 0);
  return {
    name,
    idleFps: fps,
    idleP50: idle?.p50 ?? 0,
    idleP95: idle?.p95 ?? 0,
    idleOver50: idle?.over50 ?? 0,
    toggleFramesMed: median(toggles.map((t) => t.frames)),
    toggleP50Med: median(toggles.map((t) => t.p50)),
    toggleP95Med: median(toggles.map((t) => t.p95)),
    toggleMaxMax: toggles.reduce((a, t) => Math.max(a, t.max || 0), 0),
    toggleOver50: sum('over50'),
    toggleOver100: sum('over100'),
    toggleWindows: toggles.length,
    rapidFrames: rapid?.frames ?? 0,
    rapidP95: rapid?.p95 ?? 0,
    rapidMax: rapid?.max ?? 0,
    rapidOver50: rapid?.over50 ?? 0,
  };
}

export function formatSweepRow(r) {
  const pad = (v, n) => String(v).padEnd(n);
  return `${pad(r.name, 22)} idle ${r.idleFps}fps p50 ${r.idleP50} >50:${r.idleOver50} | sw ${r.toggleFramesMed}f p95 ${r.toggleP95Med} max ${r.toggleMaxMax} >50:${r.toggleOver50} >100:${r.toggleOver100} | rapid ${r.rapidFrames}f p95 ${r.rapidP95} max ${r.rapidMax} >50:${r.rapidOver50}`;
}

// Reads every candidate against the baselines directly before and after it (mean of the
// two). Device state drifts during a run (thermal, OS frame-rate governor), so a candidate
// is only compared to its neighbours in time, and the spread between the two neighbouring
// baselines is reported as the noise floor of that comparison.
export function annotateSweepDeltas(rows) {
  const isBase = (r) => r.flagsKey === '-' || r.name === 'baseline' || r.name.startsWith('baseline');
  return rows.map((row, i) => {
    if (isBase(row) || row.reference) return { ...row };
    let prev = null;
    let next = null;
    for (let k = i - 1; k >= 0; k--) if (isBase(rows[k])) { prev = rows[k]; break; }
    for (let k = i + 1; k < rows.length; k++) if (isBase(rows[k])) { next = rows[k]; break; }
    if (!prev || !next) return { ...row, vsBase: null };
    const mean = (key) => (prev[key] + next[key]) / 2;
    const spread = (key) => Math.abs(prev[key] - next[key]);
    const keys = ['idleFps', 'toggleP95Med', 'toggleOver100', 'toggleOver50', 'rapidP95', 'rapidOver50'];
    const vsBase = {};
    for (const key of keys) {
      vsBase[key] = { delta: round1(row[key] - mean(key)), noise: round1(spread(key)) };
    }
    return { ...row, vsBase };
  });
}

export function formatDeltaRow(r) {
  if (!r.vsBase) return null;
  const f = (k, unit = '') => `${r.vsBase[k].delta > 0 ? '+' : ''}${r.vsBase[k].delta}${unit} (noise ${r.vsBase[k].noise})`;
  return `${String(r.name).padEnd(22)} vs neighbours: idle ${f('idleFps', 'fps')} | sw p95 ${f('toggleP95Med')} >100 ${f('toggleOver100')} | rapid p95 ${f('rapidP95')}`;
}

// controls: { probe, getFlags, setFlags, getMode, setMode }. Restores flags and mode at the
// end (also when aborted). `wait` and `configs` are injectable for tests.
export async function runArchiveSweep(controls, {
  configs = SWEEP_CONFIGS,
  wait = (ms) => new Promise((r) => setTimeout(r, ms)),
  onProgress = () => {},
  isAborted = () => false,
  idleMs = 4000,
  switchMs = 1400,
  switches = 3,
  rapidMs = 2500,
} = {}) {
  const originalFlags = controls.getFlags();
  const originalMode = controls.getMode();
  const rows = [];
  try {
    for (let i = 0; i < configs.length; i++) {
      if (isAborted()) break;
      const { name, flags = [], reference = false } = configs[i];
      onProgress({ index: i, total: configs.length, name });
      if (reference) {
        controls.setFlags([]);
        controls.setMode('singleplayer');
        controls.setReference?.(true);
        await wait(900);
        const idleRef = await controls.probe.beginAsync(`${name}/idle`, { durationMs: idleMs });
        controls.setReference?.(false);
        const refRow = { ...summarizeSweepConfig(name, { idle: idleRef, toggles: [], rapid: null }), reference: true, flagsKey: 'ref' };
        rows.push(refRow);
        onProgress({ index: i, total: configs.length, name, row: refRow });
        continue;
      }
      controls.setFlags(flags);
      controls.setMode('singleplayer');
      await wait(900);

      const idle = await controls.probe.beginAsync(`${name}/idle`, { durationMs: idleMs });
      const toggles = [];
      for (let k = 0; k < switches && !isAborted(); k++) {
        const a = controls.probe.beginAsync(`${name}/SP->MP`, { durationMs: switchMs });
        controls.setMode('multiplayer');
        const ra = await a;
        if (ra) toggles.push(ra);
        await wait(300);
        const b = controls.probe.beginAsync(`${name}/MP->SP`, { durationMs: switchMs });
        controls.setMode('singleplayer');
        const rb = await b;
        if (rb) toggles.push(rb);
        await wait(300);
      }
      const rapidPromise = controls.probe.beginAsync(`${name}/rapid`, { durationMs: rapidMs });
      controls.setMode('multiplayer');
      await wait(350);
      controls.setMode('singleplayer');
      await wait(350);
      controls.setMode('multiplayer');
      await wait(350);
      controls.setMode('singleplayer');
      const rapid = await rapidPromise;

      const row = { ...summarizeSweepConfig(name, { idle, toggles, rapid }), flagsKey: serializeArchivePerfFlags(flags) || '-' };
      rows.push(row);
      onProgress({ index: i, total: configs.length, name, row });
    }
  } finally {
    controls.setReference?.(false);
    controls.setFlags(originalFlags);
    controls.setMode(originalMode);
  }
  return rows;
}

// ---- HUD (the only DOM-touching part) ---------------------------------------

function fmt(r) {
  if (!r.frames) return `${r.label}: no frames (tab in background?)`;
  return `${r.label}  p50 ${r.p50}  p95 ${r.p95}  max ${r.max}  >17:${r.over17}  >33:${r.over33}  (${r.frames}f)`;
}

// Small always-on-top read-out for testing on a phone without a Web Inspector
// cable. `controls` is provided by the DeckBuilder:
//   { getFlags(), setFlags(list), getMode(), setMode(mode), setReference(on), toggleMode(), probe }
export function mountProbeHud(controls, doc = globalThis.document) {
  if (!doc || !doc.body || doc.getElementById('archive-probe-hud')) return null;
  const hud = doc.createElement('div');
  hud.id = 'archive-probe-hud';
  hud.style.cssText = [
    'position:fixed', 'left:6px', 'bottom:6px', 'z-index:2147483000',
    'max-width:min(96vw,600px)', 'max-height:52vh', 'overflow:auto', 'padding:6px 8px', 'border-radius:6px',
    'background:rgba(0,0,0,.82)', 'border:1px solid rgba(0,240,255,.5)',
    'color:#9fe', 'font:10px/1.35 ui-monospace,Menlo,monospace', 'white-space:pre-wrap',
    'pointer-events:auto',
  ].join(';');

  const out = doc.createElement('div');
  const chips = doc.createElement('div');
  const buttons = doc.createElement('div');
  chips.style.cssText = buttons.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;margin-top:4px';
  hud.append(out, chips, buttons);

  const lines = [];
  const render = () => { out.textContent = lines.slice(-14).join('\n') || 'archive probe ready - switch mode or press a button'; };
  const pushResult = (r) => { lines.push(fmt(r)); render(); try { console.log('[archive-probe]', r); } catch { /* ignore */ } };
  controls.probe.onResult = pushResult;

  const mkBtn = (text, onClick, parent) => {
    const b = doc.createElement('button');
    b.type = 'button';
    b.textContent = text;
    b.style.cssText = 'font:inherit;padding:3px 6px;border-radius:4px;border:1px solid rgba(0,240,255,.5);background:rgba(15,23,42,.9);color:#cff;touch-action:manipulation';
    b.addEventListener('click', (e) => { e.stopPropagation(); onClick(b); });
    parent.appendChild(b);
    return b;
  };

  const paintChips = () => {
    chips.textContent = '';
    const active = controls.getFlags();
    for (const flag of ARCHIVE_PERF_FLAGS) {
      const on = active.includes(flag);
      const b = mkBtn(`${on ? '[x]' : '[ ]'} ${flag}`, () => {
        const next = on ? active.filter((f) => f !== flag) : [...active, flag];
        controls.setFlags(next);
        paintChips();
      }, chips);
      b.title = ARCHIVE_PERF_FLAG_INFO[flag];
    }
  };

  mkBtn('IDLE 10s', () => controls.probe.begin('idle', { durationMs: 10000 }), buttons);
  mkBtn('REF (empty)', async () => {
    controls.setReference?.(true);
    const r = await controls.probe.beginAsync('reference(empty page)', { durationMs: 4000 });
    controls.setReference?.(false);
    if (!r) { lines.push('reference: another window is running'); render(); }
  }, buttons);
  mkBtn('TOGGLE', () => controls.toggleMode(), buttons);
  mkBtn('RAPID x3', () => {
    controls.probe.begin('rapid', { durationMs: 2500 });
    controls.toggleMode();
    setTimeout(() => controls.toggleMode(), 350);
    setTimeout(() => controls.toggleMode(), 700);
  }, buttons);
  let sweepRunning = false;
  let sweepAbort = false;
  mkBtn('SWEEP (~3 min)', async (btn) => {
    if (sweepRunning) { sweepAbort = true; btn.textContent = 'stopping...'; return; }
    sweepRunning = true;
    sweepAbort = false;
    btn.textContent = 'STOP sweep';
    lines.push('sweep started - keep the screen on and do not touch the phone');
    render();
    try {
      const rows = await runArchiveSweep(controls, {
        isAborted: () => sweepAbort,
        onProgress: ({ index, total, name, row }) => {
          if (!row) { lines.push(`sweep ${index + 1}/${total}: ${name} ...`); render(); }
        },
      });
      const annotated = annotateSweepDeltas(rows);
      globalThis.__archiveSweep = annotated;
      lines.push('--- SWEEP (idle | 6 switches: median p95, hitches summed | rapid) ---');
      for (const r of annotated) lines.push(formatSweepRow(r));
      lines.push('--- candidate vs neighbouring baselines (noise = spread of those baselines) ---');
      for (const r of annotated) { const d = formatDeltaRow(r); if (d) lines.push(d); }
      lines.push('press COPY to export all windows plus these tables');
    } catch (err) {
      lines.push(`sweep failed: ${err?.message || err}`);
    }
    sweepRunning = false;
    btn.textContent = 'SWEEP (~3 min)';
    render();
  }, buttons);
  mkBtn('COPY', () => {
    const rows = globalThis.__archiveSweep || [];
    const payload = { table: rows.map(formatSweepRow), deltas: rows.map(formatDeltaRow).filter(Boolean), rows, windows: controls.probe.results };
    const text = JSON.stringify(payload, null, 1);
    try { globalThis.navigator?.clipboard?.writeText?.(text); } catch { /* ignore */ }
    lines.push(`copied ${controls.probe.results.length} results`);
    render();
  }, buttons);
  mkBtn('hide', () => { hud.style.display = 'none'; }, buttons);

  paintChips();
  render();
  doc.body.appendChild(hud);
  return hud;
}
